import React, { useRef, FC, ReactNode, useEffect } from 'react';

import { useVirtual } from 'react-virtual';

import { Box } from '@adsk/alloy-react-box';

const LIST_MAX_HEIGHT = 450;

export type VirtualizedListProps = {
  renderItem?: (index: number) => ReactNode;
  renderLoader?: () => ReactNode;
  fetchNextPage?: () => void;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  size: number;
  renderError?: () => ReactNode;
};

const VirtualizedList: FC<VirtualizedListProps> = ({
  renderItem,
  renderLoader,
  hasNextPage,
  fetchNextPage,
  isFetchingNextPage,
  size,
  renderError,
}) => {
  const parentRef = useRef<HTMLDivElement>(null);
  const pageSizeWithLoader =
    hasNextPage || isFetchingNextPage ? size + 1 : size;
  const pageSize = hasNextPage || isFetchingNextPage ? size - 1 : size;
  const rowVirtualizer = useVirtual({
    size: pageSizeWithLoader,
    parentRef,
  });

  useEffect(() => {
    const lastItem =
      rowVirtualizer.virtualItems[rowVirtualizer.virtualItems.length - 1];
    const isLastItemVisible = lastItem?.index > pageSize;

    if (!lastItem) {
      return;
    }

    if (isLastItemVisible && hasNextPage && !isFetchingNextPage) {
      fetchNextPage?.();
    }
  }, [
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    rowVirtualizer.virtualItems,
    pageSize,
  ]);

  return (
    <Box
      ref={parentRef}
      minHeight="120px"
      maxHeight={LIST_MAX_HEIGHT}
      overflow="auto"
      data-testid="virtualized-list"
    >
      <Box height={rowVirtualizer.totalSize} width="100%" position="relative">
        {rowVirtualizer.virtualItems.map((virtualRow) => (
          <div
            key={virtualRow.index}
            ref={virtualRow.measureRef}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              transform: `translateY(${virtualRow.start}px)`,
            }}
          >
            <div>
              {virtualRow.index > pageSize
                ? isFetchingNextPage
                  ? renderLoader?.()
                  : null // Nothing more to load
                : renderItem?.(virtualRow.index)}
            </div>
          </div>
        ))}
      </Box>
      {renderError?.()}
    </Box>
  );
};

VirtualizedList.displayName = 'VirtualizedList';

export default VirtualizedList;
